import React from "react";
import { useCopies } from "shared/hooks/useCopies";
import Size from "shared/enums/Size";
import { TicketType } from "./type";
import useTicketPageService from "./service";
import { TicketApiResponseType } from "apps/registration/types/regFormApiResponse";
import NoTicketsAvailable from "./noTicketsAvailable";
import sortBy from "lodash/sortBy";
import QueryParamKey from "shared/enums/QueryParamKey";
import { RegistrationFormType } from "shared/enums/RegistrationFormType";
import { Modal, ModalBody, ModalHeader } from "shared/components/modal";
import { Base64Util } from "shared/utils/base64";
import TicketCard from "shared/components/ticketCard";
import { TicketCardFlowType } from "shared/components/ticketCard/enum";

// todo:sk check button variant css
// discuss with RR

/**
 * @description Component to list out tickets based on type(hosted, application, payment)
 * @param tickets Array of tickets to be shown
 *
 */

export default function TicketList(props: {
  ticketList: Array<TicketApiResponseType>;
  showSingleBuyButtonAtOnce?: boolean;
}) {
  const service = useTicketPageService(props.ticketList);

  if (props.ticketList.length === 0) {
    return <NoTicketsAvailable />;
  }

  return (
    <React.Fragment>
      {Object.keys(service.ticketListGroupedBySections).length > 0 &&
        Object.values(service.ticketListGroupedBySections).map((ticketListBySection, index) => (
          <section className="ticketsPage" key={index}>
            <div className="ticketsPage_wrapper">
              <div className="ticketsPage_applicableWrapper ticketAutoHeight">
                {sortBy(ticketListBySection, ["ticketPageDisplayOrder", ticket => ticket.title?.toLowerCase()]).map(
                  ticket => (
                    <Ticket
                      key={ticket.id}
                      ticket={ticket}
                      getTicketUrl={service.getTicketUrl}
                      showSingleBuyButtonAtOnce={props.showSingleBuyButtonAtOnce}
                    />
                  )
                )}
              </div>
            </div>
          </section>
        ))}
    </React.Fragment>
  );
}

export function Ticket(props: TicketType) {
  const copies = useCopies();
  const [viewBulkTicket, setViewBulkTicket] = React.useState(false);

  return (
    <React.Fragment>
      <TicketCard
        id={props.ticket.id}
        title={props.ticket.title}
        ticketDisplayColour={props.ticket.ticketDisplayColour}
        termsAndConditions={props.ticket.termsAndConditions}
        subRole={props.ticket.subRole}
        description={props.ticket.ticketsPageBody}
        bulkPurchaseAllowed={props.ticket.bulkPurchaseAllowed}
        ticketCardFlowType={TicketCardFlowType.REG_GET_TICKET}
        ticketUrl={props.getTicketUrl(props.ticket)}
        buyTicketClickHandler={() => setViewBulkTicket(true)}
        currentPrice={props.ticket.applicableRate?.basePrice}
        availablePrivileges={props.ticket.accessPrivileges}
        applicableRate={props.ticket.applicableRate}
        applicationFlow={props.ticket.applicationFlow}
        paymentFlow={props.ticket.paymentFlow}
        workflowType={props.ticket.workflowType}
        showSingleBuyButtonAtOnce={props.showSingleBuyButtonAtOnce}
      />
      {viewBulkTicket && (
        <Modal size={Size.EXTRA_SMALL} className="regBulkTicketModal">
          <ModalHeader left={copies.get("bulk.ticket.buy.in.bulk")} onClose={() => setViewBulkTicket(false)} />
          <ModalBody>
            <BulkTicket {...props} />
          </ModalBody>
        </Modal>
      )}
    </React.Fragment>
  );
}

export function BulkTicket(props: TicketType) {
  const copies = useCopies();
  const { bulkTicketUrl } = React.useMemo(() => {
    return {
      bulkTicketUrl:
        props.getTicketUrl(props.ticket) +
        `&${QueryParamKey.REGISTRATION_FORM_TYPE}=${Base64Util.encode(RegistrationFormType.BULK)}`
    };
  }, [props]);

  return (
    <div className="ticketsPage_wrapper ticketsPage_bulkTicketWrappper">
      <div className="ticketsPage_applicableWrapper ticketAutoHeight">
        <TicketCard
          id={props.ticket.id}
          ticketUrl={bulkTicketUrl}
          title={props.ticket.bulkTitle}
          availablePrivileges={props.ticket.accessPrivileges}
          ticketDisplayColour={props.ticket.ticketDisplayColour}
          ticketCardFlowType={TicketCardFlowType.REG_BULK_TICKET}
          termsAndConditions={props.ticket.bulkTermsAndConditions}
          description={props.ticket.bulkTicketsPageBody ?? copies.get("tickets.page.bulk.ticket.page.body.empty")}
        />
      </div>
    </div>
  );
}
